import { fetchMapData } from "../misc/fetchMapData";
import React, { useCallback, useMemo, useState } from "react";
import {
    FilterMetadataApiWithCountReturn,
    MapData,
    findBounds,
    DEFAULT_MAX_BOUNDS,
    MAP_MAX_ZOOM,
    MAP_CENTER,
    buildUri,
    ROUTE,
    Color,
    SEPARATOR
} from "../misc";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import { BaseMap, DataLayer, IS_SCHEME_GEOJSON as _IS_SCHEME_GEOJSON, TILENAME } from "./BaseMap";
import { SpatialValue } from "./types";
import * as d3 from "d3";
import _ from "lodash";
import { useAsync } from "../../Components/hooks";

const PWS = "Potable water scheme";
const SCHEME_COLOR = "#004061";

interface MapProps {
    maxBounds?: number[];
    oceanLayerDisplay?: boolean;
    labelLayerDisplay?: boolean;
    providerName?: string;
    providerMapName?: string;
    metadata: FilterMetadataApiWithCountReturn;
    adminId?: string;
    allowOnClick?: boolean;
    maxZoom?: number;
    minZoom?: number;
    useHighlightEffectForSelection?: boolean;
    onFeatureClick?: (args: {
        code: string;
        feature: Record<string, unknown>;
        value: string;
        data: SpatialValue;
    }) => void;
    onPostFeatureClick?: (args: {
        code: string;
        feature: Record<string, unknown>;
        value: string;
        data: SpatialValue;
    }) => void;

    highlightedProviderAdminIds?: string[];
}

export const IS_SCHEME_GEOJSON = _IS_SCHEME_GEOJSON;
export const LayerInfo: Record<"provider" | "scheme" | "organisationScheme", DataLayer> = {
    provider: {
        id: "provider",
        spatialKey: "adminId",
        source: TILENAME.PROVIDER,
        "source-layer": "providers",
        type: "polygon"
    },
    scheme: {
        id: "scheme",
        // spatialKey: "lookup",
        // spatialKey: "Scheme",
        spatialKey: "Scheme ID",
        source: TILENAME.SCHEME,
        "source-layer": "schemes",
        type: "point"
    },

    organisationScheme: {
        id: "organisation-scheme",
        spatialKey: "Scheme",
        source: TILENAME.SCHEME,
        "source-layer": "schemes",
        type: "point",
        radius: 5,
        mouseOutPolygonStroke: "white",
        mouseOutPolygonStrokeWidth: 2,
        mouseOutPolygonStrokeOpacity: 1,
        mouseEnterPolygonStrokeWidth: 2,
        mouseEnterPolygonStrokeOpacity: 1
        // mouseOutPolygonFillOpacity: "red",
    }
    // dynamicScheme: {
    //     id: "dynamicScheme",
    //     spatialKey: "Scheme",
    //     source: TILENAME.SCHEME,
    //     "source-layer": "schemes",
    //     type: "point"
    // }
};

const LMap: React.FC<MapProps> = ({
    maxBounds,
    // providerName,
    providerMapName,
    metadata,
    adminId,
    useHighlightEffectForSelection = false,
    highlightedProviderAdminIds,
    onFeatureClick,
    onPostFeatureClick,
    allowOnClick = true,
    maxZoom = MAP_MAX_ZOOM,
    minZoom,
    labelLayerDisplay = true,
    oceanLayerDisplay = true
}) => {
    const history = useHistory();

    const [mapData, setMapData] = useState<MapData>({
        adminIds: [],
        colorMappings: {},
        spatialValues: []
    });

    const load = useCallback(
        async (signal?: AbortSignal): Promise<MapData> => {
            // load map data
            const _mapData = await fetchMapData(metadata, { signal });

            const colorMappings = _mapData?.colorMappings ?? {};

            const adminIdToOne =
                highlightedProviderAdminIds?.reduce((prev, current) => {
                    prev[current] = 1;
                    return prev;
                }, {}) ?? {};

            // highlighted provider spatial values
            const highlightedProviderValues = _mapData?.spatialValues?.filter(d => {
                return adminIdToOne?.[d?.adminId] === 1;
            });
            const adminIdToProviderValues = _.keyBy(highlightedProviderValues, d => d.adminId);

            // update colors for highlighted entities (keep opacity as 1)
            highlightedProviderAdminIds?.forEach(adminId => {
                const sourceCategoryColor = colorMappings?.[adminIdToProviderValues?.[adminId]?.category];
                if (sourceCategoryColor != null) {
                    const colors = d3.rgb(sourceCategoryColor);
                    const newColor = `rgba(${colors.r}, ${colors.g}, ${colors.b}, 1)`;
                    colorMappings[adminId] = newColor as unknown as Color;
                }
            });

            // update colors for non highlighted entities (update opacity)
            _.keys(colorMappings).forEach(key => {
                // if there are some highlights, lower opacity of everything except for highlighted entities
                if (
                    adminIdToOne[key] !== 1 &&
                    // highlighted elements as array is initialised
                    highlightedProviderAdminIds?.length >= 0
                ) {
                    const color = colorMappings[key];
                    const colors = d3.rgb(color);
                    const newColor = `rgba(${colors.r}, ${colors.g}, ${colors.b}, 0.1)`;
                    colorMappings[key] = newColor as unknown as Color;
                }
            });

            // update categories to assign adminIds to provider categories
            _mapData?.spatialValues?.forEach(d => {
                if (adminIdToOne?.[d?.adminId] === 1) {
                    d.category = d.adminId;
                }
            });

            // make selected admin ids as same effects as highlights
            const selectedAdminIds = [adminId].filter(d => d != null && d.trim() !== "");
            const selectedAdminIdToOne =
                selectedAdminIds.reduce((prev, current) => {
                    prev[current] = 1;
                    return prev;
                }, {}) ?? {};

            // highlighted provider spatial values
            const selectedProviderValues = _mapData?.spatialValues?.filter(d => {
                return selectedAdminIdToOne?.[d?.adminId] === 1;
            });
            const selectedAdminIdToProviderValues = _.keyBy(selectedProviderValues, d => d.adminId);

            // update colors for highlighted entities (keep opacity as 1)
            selectedAdminIds?.forEach(adminId => {
                const sourceCategoryColor = colorMappings?.[selectedAdminIdToProviderValues?.[adminId]?.category];
                if (sourceCategoryColor != null) {
                    const colors = d3.rgb(sourceCategoryColor);
                    const newColor = `rgba(${colors.r}, ${colors.g}, ${colors.b}, 1)`;
                    colorMappings[adminId] = newColor as unknown as Color;
                }
            });

            // update colors for non highlighted entities (update opacity)
            _.keys(colorMappings).forEach(key => {
                // if there are some highlights, lower opacity of everything except for highlighted entities
                if (selectedAdminIdToOne[key] !== 1 && selectedAdminIds != null && selectedAdminIds.length > 0) {
                    const color = colorMappings[key];
                    const colors = d3.rgb(color);
                    const newColor = `rgba(${colors.r}, ${colors.g}, ${colors.b}, 0.1)`;
                    colorMappings[key] = newColor as unknown as Color;
                }
            });

            // update categories to assign adminIds to provider categories
            _mapData?.spatialValues?.forEach(d => {
                if (selectedAdminIdToOne?.[d?.adminId] === 1) {
                    d.category = d.adminId;
                }
            });

            // console.log("retrieve map data");
            return _mapData;
        },
        [metadata, highlightedProviderAdminIds, adminId]
    );

    useAsync(load, setMapData);

    const { adminIds, colorMappings, spatialValues } = mapData;

    const defaultOnFeatureClick: (args: { code: string; feature: Record<string, unknown> }) => void = useCallback(
        ({ code, feature }) => {
            if (code == null || !allowOnClick || feature?.["source"] === LayerInfo.organisationScheme.source) return;

            history.push(
                buildUri(
                    ROUTE.PROVIDER_INFO,
                    {},
                    {
                        id: code
                    }
                )
            );
        },
        [allowOnClick, history]
    );

    const _onFeatureClick: (args: {
        code: string;
        feature: Record<string, unknown>;
        value: string | number;
        data: unknown;
    }) => void = useCallback(
        ({ code, feature, value, data, ...rest }) => {
            if (code == null || feature?.["source"] === LayerInfo.organisationScheme.source) return;

            if (!onFeatureClick) {
                defaultOnFeatureClick({
                    code,
                    feature,
                    ...rest
                });
            } else {
                onFeatureClick({
                    ...rest,
                    code,
                    feature,
                    value: value as string,
                    data: data as SpatialValue
                });
            }

            onPostFeatureClick != null &&
                onPostFeatureClick({
                    ...rest,
                    code,
                    feature,
                    value: value as string,
                    data: data as SpatialValue
                });
        },
        [onFeatureClick, onPostFeatureClick, defaultOnFeatureClick]
    );

    const flyToAdminIds = useMemo(() => {
        return [adminId].filter(d => d != null && d.trim() !== "");
    }, [adminId]);

    const selectedAdminId = useMemo(() => {
        return adminId ?? undefined;
    }, [adminId]);

    // const [layers, setLayers] = useState<DataLayer[]>([LayerInfo.provider]);

    // useEffect(() => {
    //     if (providerName != null && providerName?.trim() !== "") {
    //         const schemeLayer: DataLayer = {
    //             ...LayerInfo.organisationScheme,
    //             id: `${LayerInfo.organisationScheme.id}`,
    //             featureGroupingSelection: ["in", "lookup", _.join([providerName, PWS], ` ${SEPARATOR} `)]
    //         };

    //         setLayers([LayerInfo.provider, schemeLayer]);
    //     }
    // }, [providerName]);

    const layers: DataLayer[] = useMemo(() => {
        const schemeLayer: DataLayer =
            providerMapName == null || providerMapName?.trim() === ""
                ? null
                : {
                      ...LayerInfo.organisationScheme,
                      id: `${LayerInfo.organisationScheme.id}`,
                      featureGroupingSelection: ["in", "lookup", _.join([providerMapName, PWS], ` ${SEPARATOR} `)]
                  };

        const _layers = [LayerInfo.provider, schemeLayer].filter(d => d != null);

        return _layers;
    }, [providerMapName]);

    const setupExtraLayers: (map: mapboxgl.Map) => void = useCallback(map => {
        const schemeLayer = map.getLayer(LayerInfo.organisationScheme.id);
        if (schemeLayer) {
            map.setPaintProperty(LayerInfo.organisationScheme.id, "circle-color", SCHEME_COLOR);
        }

        const hoverSchemeLayer = map.getLayer(`hover-${LayerInfo.organisationScheme.id}`);
        if (hoverSchemeLayer) {
            const hoverColor = d3.hsl(SCHEME_COLOR).brighter(1).toString();

            map.setPaintProperty(`hover-${LayerInfo.organisationScheme.id}`, "circle-color", hoverColor);
            map.setPaintProperty(`hover-${LayerInfo.organisationScheme.id}`, "circle-opacity", 1);
        }
    }, []);

    // console.log(
    //     providerName,
    //     adminId,
    //     metadata,
    //     maxZoom,
    //     minZoom,
    //     highlightedProviderAdminIds,
    //     useHighlightEffectForSelection,
    //     allowOnClick,
    //     mapData,
    //     onFeatureClick
    // );
    return (
        <>
            <BaseMap
                oceanLayerDisplay={oceanLayerDisplay}
                labelLayerDisplay={labelLayerDisplay}
                meta={metadata}
                displayAdminIds={adminIds}
                // displayAdminIds={[]}
                selectedAdminId={selectedAdminId}
                setupExtraLayers={setupExtraLayers}
                minZoom={minZoom ?? undefined}
                maxZoom={maxZoom}
                center={MAP_CENTER}
                useHighlightEffectForSelection={useHighlightEffectForSelection}
                flyToAdminIds={flyToAdminIds}
                maxBounds={maxBounds ?? DEFAULT_MAX_BOUNDS}
                hover={({ d3Tooltip, meta, data, features }) => {
                    const element = d3Tooltip && d3Tooltip.node ? d3Tooltip.node() : null;

                    let content = (
                        <>
                            <span>Loading</span>
                        </>
                    );

                    if (
                        (!data || !meta) &&
                        features &&
                        features?.[0]?.["source"] === LayerInfo.organisationScheme.source
                    ) {
                        const schemeNames = [features?.map(d => d?.properties?.["Scheme"])?.[0]].filter(d => d != null);
                        if (schemeNames?.length === 1) {
                            content = (
                                <>
                                    <span>Scheme: </span>
                                    <span>{`${_.join(schemeNames, ", ")}`}</span>
                                </>
                            );
                        } else if (schemeNames?.length > 1) {
                            content = (
                                <>
                                    <span>Schemes: </span>
                                    <span>{`${_.join(schemeNames, ", ")}`}</span>
                                </>
                            );
                        }
                    } else {
                        const spatialValue = data as SpatialValue;
                        const metadata = meta as FilterMetadataApiWithCountReturn;

                        if (spatialValue) {
                            const { name, category: providerTypeValue } = spatialValue;
                            const providerType = metadata?.["providerType"].options?.find(({ value }) => {
                                return value === providerTypeValue;
                            });

                            content = (
                                <>
                                    {name}{" "}
                                    {providerType != null && (
                                        <>
                                            <br />
                                            {providerType?.label}
                                        </>
                                    )}{" "}
                                </>
                            );
                        }
                    }
                    ReactDOM.render(content, element);
                }}
                layers={
                    // [

                    // LayerInfo.provider
                    // LayerInfo.organisationScheme
                    // ]
                    layers
                }
                colorMappings={colorMappings}
                spatialValues={spatialValues}
                findBounds={findBounds}
                onFeatureClick={_onFeatureClick}
            />
        </>
    );
};

export const Map = React.memo(LMap, (prev, next) => {
    // console.log("memo");
    return (
        prev?.maxBounds === next?.maxBounds &&
        prev?.oceanLayerDisplay === next?.oceanLayerDisplay &&
        prev?.labelLayerDisplay === next?.labelLayerDisplay &&
        prev?.providerName === next?.providerName &&
        prev?.providerMapName === next?.providerMapName &&
        prev?.adminId === next?.adminId &&
        prev?.metadata === next?.metadata &&
        prev?.maxZoom === next?.maxZoom &&
        prev?.minZoom === next?.minZoom &&
        prev?.highlightedProviderAdminIds === next?.highlightedProviderAdminIds &&
        prev?.useHighlightEffectForSelection === next?.useHighlightEffectForSelection &&
        prev?.allowOnClick === next?.allowOnClick &&
        prev?.onFeatureClick === next?.onFeatureClick &&
        prev?.onPostFeatureClick === next?.onPostFeatureClick
    );
});
